import { ensureAuthenticatedConfig } from 'serviceWorker/services/config';
import { HttpError } from 'serviceWorker/helpers/httpError';
import { apiUrl } from 'shared/apiUrl';
import { swSignals } from 'serviceWorker/events/swSignals';
import { UNPROCESSABLE_ENTITY } from 'shared/contants';
import { buildWorkerRequestHeaders } from 'serviceWorker/helpers/requestHeaders';
import { pull } from '../pull';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { isPlainObjectWithValues } from 'shared/utils/object';

const BASE_URL = `${normalizeApiUrl(apiUrl)}/v2`;

const defaultConfig: RequestInit = {
  mode: 'cors',
  cache: 'no-cache',
  credentials: 'same-origin',
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
};

function normalizeApiUrl(value: string): string {
  const trimmed = value.trim().replace(/\/$/, '');

  if (!trimmed || trimmed === '/') {
    return '/api';
  }

  return trimmed;
}

function validateEndpointPath(url: string): void {
  const normalized = url.trim();
  const pathWithoutQueryAndHash = normalized.split(/[?#]/)[0];
  const normalizedPath = pathWithoutQueryAndHash.replace(/\/+/g, '/');

  if (!normalizedPath || normalizedPath === '/') {
    throw new Error(
      'Empty API endpoint path provided to Fetcher.POST. Expected e.g. /project/{id}/...'
    );
  }
}

function normalizeEndpointPath(url: string): string {
  const normalized = url.trim();

  if (!normalized.startsWith('/')) {
    return `/${normalized}`;
  }

  return normalized;
}

/**
 * Only the token is required: at sign-in the user is downloaded before the
 * page sent the config, so the language goes once the config is stored.
 * Without a token `pull` does not send the request (`SessionTokenMissingError`).
 */
async function generateHeaders(
  headers: HeadersInit = {}
): Promise<HeadersInit> {
  const config = await ensureAuthenticatedConfig();

  return { ...buildWorkerRequestHeaders(config?.locale), ...headers };
}

function isJsonResponse(response: Response): boolean {
  return (response.headers.get('content-type') || '').includes(MIME_TYPES.JSON);
}

/** The server's error of a response, with its message when the body is JSON. */
async function toHttpError(response: Response): Promise<HttpError> {
  if (isJsonResponse(response)) {
    const parsedResponse = await response.json();
    return new HttpError(
      createMessage({ ...parsedResponse.error, status: response.status }),
      response.status,
      parsedResponse
    );
  }
  const responseText = await response.text();
  return new HttpError(createNonJsonMessage(response, responseText), response.status, responseText);
}

/** A 422 means the change was based on stale data: the project data is synced again. */
function syncWhenStale(response: Response): void {
  if (response.status === UNPROCESSABLE_ENTITY) {
    swSignals.staleProjectDataDetected.emit();
  }
}

async function toJson(response: Response): Promise<any> {
  syncWhenStale(response);
  if (response.ok && isJsonResponse(response)) {
    return response.json();
  }
  if (response.ok && (response.status === 204 || response.status === 205)) {
    return undefined;
  }
  throw await toHttpError(response);
}

function sendPostFile(
  abortController: AbortController,
  url: string,
  data: any
): Promise<any> {
  return fetch(url, {
    method: 'POST',
    headers: {
      'x-amz-acl': 'public-read',
    },
    body: data,
    signal: abortController.signal,
  }).then(async (response) => {
    // S3 can return error XML with 200 status, so we need to check both
    if (
      !response.ok ||
      response.headers.get('content-type')?.includes(MIME_TYPES.XML)
    ) {
      const responseText = await response.text();
      if (responseText.includes('<Error>')) {
        throw new Error(`S3 upload failed: ${responseText}`);
      }
    }
    return response;
  });
}

async function sendPost(
  url: string,
  data: any,
  abortController?: AbortController
): Promise<any> {
  validateEndpointPath(url);
  const endpointPath = normalizeEndpointPath(url);
  const headers = await generateHeaders();
  const response = await pull(BASE_URL + endpointPath, {
    ...defaultConfig,
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    signal: abortController?.signal,
  });

  return toJson(response);
}

async function pullGet(url: string): Promise<Response> {
  validateEndpointPath(url);
  const endpointPath = normalizeEndpointPath(url);
  const headers = await generateHeaders();
  // pull() registers requestSuspender abort internally; GET has no caller AbortSignal.
  return pull(BASE_URL + endpointPath, {
    ...defaultConfig,
    cache: 'default',
    method: 'GET',
    headers,
  });
}

async function sendGet(url: string): Promise<any> {
  return toJson(await pullGet(url));
}

/** A GET answered with text (e.g. rendered HTML); a failure still rejects with HttpError. */
async function sendGetText(url: string): Promise<string> {
  const response = await pullGet(url);
  syncWhenStale(response);
  if (!response.ok) {
    throw await toHttpError(response);
  }
  return response.text();
}

async function sendDelete(url: string): Promise<any> {
  validateEndpointPath(url);
  const endpointPath = normalizeEndpointPath(url);
  const headers = await generateHeaders();
  // pull() registers requestSuspender abort internally; DELETE has no caller AbortSignal.
  const response = await pull(BASE_URL + endpointPath, {
    ...defaultConfig,
    method: 'DELETE',
    headers,
  });

  return toJson(response);
}

async function sendPut(url: string, data: any): Promise<any> {
  validateEndpointPath(url);
  const endpointPath = normalizeEndpointPath(url);
  const headers = await generateHeaders();
  const response = await pull(BASE_URL + endpointPath, {
    ...defaultConfig,
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  return toJson(response);
}

export const Fetcher = {
  POST_FILE: sendPostFile,
  POST: sendPost,
  GET: sendGet,
  GET_TEXT: sendGetText,
  DELETE: sendDelete,
  PUT: sendPut,
};

function serializeDetails(details: string | unknown[] | unknown): string {
  if (Array.isArray(details)) {
    return details
      .map((d) => {
        if (isPlainObjectWithValues(d, ['msg'])) {
          return String(d.msg);
        }
        try {
          return JSON.stringify(d);
        } catch {
          return String(d);
        }
      })
      .join('; ');
  }
  return String(details ?? '');
}

function createMessage(
  error:
    | {
        message?: string;
        details?: string | unknown[];
        status?: number;
      }
    | undefined
): string {
  const details = error?.details !== undefined ? serializeDetails(error.details) : '';
  const parts = [error?.message, details, error?.status].filter(Boolean);
  return parts.join(' ');
}

function createNonJsonMessage(
  response: Response,
  responseText: string
): string {
  const status = response.status;
  const statusText = response.statusText || 'Unknown error';
  const bodyPreview = responseText.slice(0, 120).trim();
  const bodyPart = bodyPreview ? ` Body: ${bodyPreview}` : '';

  return `Unexpected non-JSON response (${status} ${statusText}).${bodyPart}`;
}
